import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, X } from "lucide-react";
import {
  clearNavbarNotificationsForCurrentUser,
  dismissNavbarNotification,
  getNotificationsForCurrentUser,
} from "../../utils/navbarNotifications";

function formatTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function NavbarNotifications({ darkMode = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(() => getNotificationsForCurrentUser());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);

  const refresh = useCallback(() => {
    setItems(getNotificationsForCurrentUser());
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener("hrms:navbar-notification", onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener("hrms:navbar-notification", onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-portal-navbar-notifications]")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const count = items.length;

  const handleDismiss = (id) => {
    dismissNavbarNotification(id);
    refresh();
  };

  const handleClearAll = () => {
    clearNavbarNotificationsForCurrentUser();
    refresh();
  };

  const toggleOpen = () => {
    if (!open) updateDropdownPosition();
    setOpen((v) => !v);
  };

  const dropdownPanel =
    open &&
    createPortal(
      <div
        data-portal-navbar-notifications
        className={`fixed z-[9999] w-[min(92vw,22rem)] overflow-hidden rounded-lg border shadow-xl ${
          darkMode
            ? "border-[#3C3C4C] bg-[#1D1D1D] text-gray-100"
            : "border-gray-200 bg-white text-gray-900"
        }`}
        style={{
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
        }}
      >
        <div
          className={`flex items-center justify-between border-b px-3 py-2.5 ${
            darkMode ? "border-[#3C3C4C]" : "border-gray-200"
          }`}
        >
          <span className="text-sm font-semibold">Notifications</span>
          {count > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className={`text-[11px] font-medium underline-offset-2 hover:underline ${
                darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
              }`}
            >
              Clear all
            </button>
          )}
        </div>

        <ul className="max-h-[min(60vh,320px)] overflow-y-auto">
          {count === 0 ? (
            <li
              className={`px-3 py-6 text-center text-xs ${
                darkMode ? "text-gray-500" : "text-gray-500"
              }`}
            >
              No notifications
            </li>
          ) : (
            items.map((item) => (
              <li
                key={item.id}
                className={`flex gap-2 border-b px-3 py-2.5 last:border-b-0 ${
                  darkMode ? "border-[#3C3C4C]/80" : "border-gray-100"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold leading-snug">{item.title}</p>
                  <p
                    className={`mt-0.5 text-[11px] leading-snug ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {item.message}
                  </p>
                  <p
                    className={`mt-1 text-[10px] ${
                      darkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {formatTime(item.createdAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDismiss(item.id)}
                  className={`mt-0.5 shrink-0 rounded p-1 ${
                    darkMode
                      ? "text-gray-400 hover:bg-white/10 hover:text-gray-200"
                      : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  }`}
                  aria-label="Dismiss notification"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 ${
          darkMode
            ? "border-[#3C3C4C] bg-[#1D1D1D] text-gray-200 hover:bg-[#2a2a2a]"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        }`}
        title="Notifications"
        aria-label={`Notifications${count ? `, ${count} unread` : ""}`}
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-redhalo-red px-1 text-[10px] font-bold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {dropdownPanel}
    </div>
  );
}
